import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api, TimbaAdminTournament, TimbaTournamentDetail } from '../api/client';
import { useStore } from '../stores/store';
import { TimbaBoard } from '../components/TimbaBoard';
import { BackLink, H2, Muted } from '../components/ui';

// Panel admin (/uso/torneos): todos los torneos (creador, fecha, participantes). Tocar uno → su ranking.
function fmtDate(s: string): string {
  if (!s) return '';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  return isNaN(d.getTime()) ? s.slice(0, 10) : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function UsoTorneos() {
  const navigate = useNavigate();
  const adminKey = useStore((s) => s.adminKey);
  const [list, setList] = useState<TimbaAdminTournament[]>([]);
  const [selected, setSelected] = useState('');
  const [detail, setDetail] = useState<TimbaTournamentDetail | null>(null);

  useEffect(() => {
    if (!adminKey) navigate('/acceso', { replace: true });
  }, [adminKey, navigate]);

  useEffect(() => {
    if (!adminKey) return;
    api.timbaAdminTournaments(adminKey).then((d) => setList(d.tournaments)).catch(() => {});
  }, [adminKey]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let alive = true;
    api.timbaTournament(selected, '', adminKey).then((d) => alive && setDetail(d)).catch(() => alive && setDetail(null));
    return () => {
      alive = false;
    };
  }, [selected, adminKey]);

  if (!adminKey) return null;

  return (
    <>
      <BackLink onClick={() => navigate('/uso')}>← Volver a Uso</BackLink>
      <H2>Torneos</H2>
      <Muted>Tocá un torneo para ver su ranking. ({list.length} privados + el Mundial)</Muted>

      <List>
        <li className={selected === 'global' ? 'on' : ''} onClick={() => setSelected('global')}>
          <span className="nm">🌎 Timba Mundial</span>
          <span className="meta">ranking global</span>
        </li>
        {list.map((t) => (
          <li key={t.id} className={t.id === selected ? 'on' : ''} onClick={() => setSelected(t.id)}>
            <span className="nm">🏆 {t.name}</span>
            <span className="meta">{(t.ownerNick || t.owner.slice(0, 8))} · {fmtDate(t.created)} · {t.members} part.</span>
          </li>
        ))}
      </List>

      {detail && (
        <Board>
          <div className="head">
            <span className="nm">{selected === 'global' ? '🌎' : '🏆'} {detail.name}</span>
            <span className="ct">{detail.members} {detail.members === 1 ? 'participante' : 'participantes'}</span>
          </div>
          <TimbaBoard standings={detail.standings} pub="" top={100} />
        </Board>
      )}
    </>
  );
}

const List = styled.ul`
  list-style: none;
  margin: 12px 0;
  padding: 0;
  li {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 11px 14px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 10px;
    margin-bottom: 8px;
    cursor: pointer;
  }
  li.on { border-color: ${({ theme }) => theme.colors.accent}; box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.accent}; }
  .nm { font-weight: 700; font-size: 0.95rem; }
  .meta { font-size: 0.76rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Board = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  margin-top: 14px;
  .head { display: flex; align-items: baseline; justify-content: space-between; padding: 12px 14px; background: ${({ theme }) => theme.colors.surfaceAlt}; }
  .head .nm { font-weight: 800; font-size: 1.05rem; }
  .head .ct { font-size: 0.78rem; color: ${({ theme }) => theme.colors.textMuted}; }
`;
